import { useMutation, useQuery, useQueryClient } from "react-query"
import { getDefaultFollowUpSchedule, getLinks, getProposalSettings, updateDefaultFollowUpSchedule, updateGeneralSettings, updateLinks, updatePaymentScheduleOptions, updateTermsAndConditions } from "./proposalSettingsApi"
import { AutomationSchedule, GeneralSettings, PaymentScheduleOptions, ProposalTermsAndConditions, TenantLink } from "./proposalSettings"
import { notification } from "antd"

export const useProposalSettingsQuery = () => {
  return useQuery(['proposal-settings'], {
    queryFn: () => getProposalSettings(),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    onError() {
      notification.error({ message: 'There was a problem loading proposal settings.' })
    }
  })
}

export const useUpdatePaymentScheduleOptionsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (settings: PaymentScheduleOptions) => updatePaymentScheduleOptions(settings),
    onSuccess: () => {
      queryClient.invalidateQueries(['proposal-settings'])
    },
    onError() {
      notification.error({ message: 'Failed to update proposal invoice schedule options' })
    },
  })
}

export const useUpdateGeneralSettingsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (settings: GeneralSettings) => updateGeneralSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries(['proposal-settings'])
    },
    onError() {
      notification.error({ message: 'Failed to update proposal general settings' })
    },
  })
}

export const useUpdateTermsAndConditionsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (terms: ProposalTermsAndConditions) => updateTermsAndConditions(terms),
    onSuccess: () => {
      queryClient.invalidateQueries(['proposal-settings'])
    },
    onError() {
      notification.error({ message: 'Failed to update proposal terms and conditions' })
    },
  })
}

export const useProposalLinksQuery = () => {
  return useQuery(['proposal-settings-links'], {
    queryFn: () => getLinks(),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    onError() {
      notification.error({ message: 'There was a problem loading proposal links.' })
    }
  })
}

export const useUpdateLinksMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (links: TenantLink[]) => updateLinks(links),
    onSuccess: () => {
      queryClient.invalidateQueries(['proposal-settings-links'])
    },
    onError() {
      notification.error({ message: 'Failed to update proposal links' })
    },
  })
}

export const useDefaultFollowUpScheduleQuery = () => {
  return useQuery(['proposal-settings-default-follow-up'], {
    queryFn: () => getDefaultFollowUpSchedule(),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    onError() {
      notification.error({ message: 'There was a problem loading the proposal follow-up schedule.' })
    }
  })
}

export const useUpdateDefaultFollowUpScheduleMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (schedule: AutomationSchedule) => updateDefaultFollowUpSchedule(schedule),
    onSuccess: () => {
      queryClient.invalidateQueries(['proposal-settings-default-follow-up'])
    },
    onError() {
      notification.error({ message: 'Failed to update proposal default follow-up schedule' })
    },
  })
}